import { useCallback, useEffect, useState } from "react";
import { fetchHospitalAdmissions } from "../api/hospitalApi";
import type { CompanyAdmissionsQuery } from "../api/types";
import { hospitalAdmissions } from "../data";
import { getAuthToken } from "../auth";
import type { Admission } from "../types";
import { mapApiAdmission } from "./useEnterpriseAdmissions";

export function useHospitalAdmissions(filters: CompanyAdmissionsQuery = {}) {
  const [admissions, setAdmissions] = useState<Admission[]>(() => [...hospitalAdmissions]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);
  const [total, setTotal] = useState(hospitalAdmissions.length);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setAdmissions([...hospitalAdmissions]);
      setTotal(hospitalAdmissions.length);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchHospitalAdmissions(filters);
      const mapped = (response.admissions ?? []).map(mapApiAdmission);
      setAdmissions(mapped);
      setTotal(response.total ?? mapped.length);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les admissions.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.date_start, filters.date_end, filters.search]);

  useEffect(() => {
    void load();
  }, [load]);

  return { admissions, total, loading, error, usingApi, reload: load };
}
